import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { clientApi } from '@/config/axios';
import { useAuth } from '@/lib/auth/AuthContext';
import { DocumentViewerSheet } from '@/features/emr/components/DocumentViewerSheet';
import type { GeneratedDocumentOut } from '@/features/emr/api/documents-api';
import { ArrowLeft } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { useOrgDateFormat } from '@/lib/timezone';
import { formatCalendarDate } from '@/lib/utils/date';
import { Card, CardContent } from '@/components/ui/card';

export interface InlineDocumentItem {
  content: string | null;
  date: TimelineDate;
  id: string;
  name: string;
  source: 'generated' | 'uploaded';
  type: string;
}

/**
 * A timeline date is either an instant (`generated_at`) or a calendar date
 * (`document_date`, `encounter_date`). They render and sort differently, and
 * the two used to share one `string` field — which meant whichever rule was
 * applied was wrong for half the values.
 */
export type TimelineDate = { kind: 'instant'; value: string } | { kind: 'calendar'; value: string };

interface InlineDocumentViewerProps {
  item: InlineDocumentItem;
  onBack: () => void;
}

export function InlineDocumentViewer({ item, onBack }: InlineDocumentViewerProps) {
  const [currentReport, setCurrentReport] = useState<GeneratedDocumentOut | null>(null);
  const current = currentReport?.id === item.id ? currentReport : null;
  const content = current?.content ?? item.content;
  return (
    <div className="space-y-3">
      <Button size="sm" variant="ghost" className="h-7 gap-1 text-xs" onClick={onBack}>
        <ArrowLeft className="h-3.5 w-3.5" />
        Back to list
      </Button>

      <div>
        <h3 className="text-sm font-medium">{current?.title || item.name}</h3>
        <p className="text-xs text-muted-foreground">
          <TimelineDateLabel date={item.date} />
        </p>
      </div>

      {item.source === 'generated' && <GeneratedReportActions item={item} onReport={setCurrentReport} />}
      <Card>
        <CardContent className="p-3">
          {content ? (
            <div className="prose prose-sm max-w-none whitespace-pre-wrap text-xs">{content}</div>
          ) : (
            <p className="text-sm text-muted-foreground">No content available for this document.</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export function TimelineDateLabel({ date }: { date: TimelineDate }) {
  const { formatInstant } = useOrgDateFormat();
  return <>{date.kind === 'calendar' ? formatCalendarDate(date.value) : formatInstant(date.value, 'MMM d, yyyy')}</>;
}

function GeneratedReportActions({
  item,
  onReport,
}: {
  item: InlineDocumentItem;
  onReport: (report: GeneratedDocumentOut) => void;
}) {
  const [open, setOpen] = useState(false);
  const { organization } = useAuth();
  const { data: report } = useQuery<GeneratedDocumentOut>({
    queryKey: ['signing-document', organization?.organization_id, item.id],
    enabled: item.source === 'generated' && !!organization,
    queryFn: async () =>
      (await clientApi.get(`/api/v1/organizations/${organization!.organization_id}/documents/${item.id}`)).data,
  });
  useEffect(() => {
    if (report) onReport(report);
  }, [report, onReport]);
  return (
    <>
      {report && (
        <>
          <p className="text-sm text-muted-foreground">
            {report.signed_version_id
              ? `Signed version ${report.version_number} · ${report.signer_name}`
              : 'Draft — unsigned'}
          </p>
          <Button variant="outline" onClick={() => setOpen(true)}>
            Open report and signing history
          </Button>
          <DocumentViewerSheet open={open} document={report} onOpenChange={setOpen} />
        </>
      )}
    </>
  );
}
